// ─────────────────────────────────────────────────────────────────────────────
// Artist CNTRL, React FOUNDATION contacts data layer.
//
// Powers CD's redesigned Contacts screen. Design-independent: returns typed
// models, never markup. Each fetcher faithfully mirrors the legacy desktop
// wiring (site/js/contacts-wiring.js) against the live workers, with the exact
// legacy caller + worker return + client read documented per-function below.
//
//   Crew    → fetchContacts()            GET /api/data/contacts   (lib/dashboard.ts, reused)
//   Teams   → fetchTeams()               GET /api/me/orgs
//   Friends → fetchFriends()             GET /api/friends
//   Top 8   → deriveTop8() over friends  (first 8 accepted, legacy renderTop8)
// ─────────────────────────────────────────────────────────────────────────────

import { STATS_WORKER, apiGet, apiPost, apiPut } from './api';
import { getToken } from './auth';
import { fetchContacts } from './dashboard';
import type { Contact, User } from './types';

// ── Avatar gradient ───────────────────────────────────────────────────────────
//
// CD's mock paints each .av / .team-logo / .av--lg with two CSS custom props
// (--a, --b). The legacy wiring derives them deterministically from the display
// name via gradientFor (contacts-wiring.js:67-77). Ported verbatim so the same
// name always yields the same colors (no hardcoded per-person colors).

/** Deterministic gradient stops for an avatar, keyed by name. Mirrors
 *  contacts-wiring.js gradientFor (:67-77). */
export function gradientFor(name: string | null | undefined): { from: string; to: string } {
  const s = String(name || '');
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) & 0xffffffff;
  const hue = Math.abs(hash) % 360;
  return {
    from: 'hsl(' + hue + ', 60%, 55%)',
    to: 'hsl(' + ((hue + 35) % 360) + ', 65%, 30%)',
  };
}

// ── Crew row (view model over a Contact) ──────────────────────────────────────
//
// VERIFICATION (Crew):
//   Legacy caller, contacts-wiring.js:184/201:
//     fetch(AUTH_WORKER + '/api/data?keys=contacts,top8', { headers: authHeaders(false) })
//     (single-key variant: fetch(AUTH_WORKER + '/api/data/contacts', …))
//   Worker return, cntrl-auth-worker.js (GET /api/data/*): jsonResponse({ success, key, data })
//     where data.value = the contacts array.
//   Legacy client read, contacts-wiring.js renderContactRow (:438-441, :503-504):
//     var name = (c.firstName || '') + (c.lastName ? ' ' + c.lastName : '');
//     if (!name.trim()) name = c.role || c.email || 'Unnamed';
//     var inviteStatus = c.inviteStatus || '';
//     var avatarSrc = c.avatarUrl || c.avatar_url || '';
//   Reuses lib/dashboard.fetchContacts() (same endpoint + same unwrap envelope).

/** A crew member ready to render into CD's .contact-row. */
export interface CrewMember {
  readonly id: string;
  /** Display name, firstName+lastName, falling back to role/email/'Unnamed'. */
  readonly name: string;
  /** Raw role string (e.g. 'band_member_av', 'manager'), RoleLabel formats it. */
  readonly role: string;
  /** Photo URL (avatarUrl/avatar_url/photoUrl/photo_url) or '' → gradient initials. */
  readonly avatarUrl: string;
  /** True when this is a pending invite → CD's "Invite sent" badge. */
  readonly pending: boolean;
  readonly email: string;
  readonly phone: string;
  /** Specific job title (e.g. "A&R", "Sync"). */
  readonly title: string;
  /** Linked AC user id (when this contact is a real account). */
  readonly userId: string;
  /** "Acting as" role tag (Tour Manager / Production Manager / Merch Manager). */
  readonly actingRole: string;
  /** acpro-teams 2026-07-27: explicit team override ('' = role default). */
  readonly team: string;
  readonly bio: string;
  readonly skills: readonly string[];
  /** Travel & loyalty, gated to Artist/AV/mgmt viewers in the profile modal. */
  readonly travel: ContactTravel;
}

export interface LoyaltyProgram { readonly program?: string; readonly number?: string; readonly tier?: string }
export interface ContactTravel {
  readonly known_traveler_number?: string;
  readonly redress_number?: string;
  readonly airline_loyalty?: ReadonlyArray<LoyaltyProgram>;
  readonly hotel_loyalty?: ReadonlyArray<LoyaltyProgram>;
}

/** The "acting as" role options (net-new feature, see screenshot 2 spec).
 *  acpro-teams 2026-07-27 (Ryan): Pro accounts have NO acting roles — the
 *  team system below replaces them. Standard AC keeps these. */
export const ACTING_ROLES: readonly string[] = ['Tour Manager', 'Production Manager', 'Merch Manager'];

/* ── AC Pro teams (acpro-teams 2026-07-27, Ryan + Rob's Pro scope) ─────────
   Pro-only. Five fixed teams; every person lands on one — auto-assigned
   from their role (TEAM_FOR_ROLE), overridable per contact (`team` field on
   the contact row, whole-blob PUT so it round-trips). Rob is still growing
   the role list — extend PRO_SEAT_ROLES + TEAM_FOR_ROLE together. */
export type ProTeam = 'a' | 'b' | 'c' | 'production' | 'drivers';
export const PRO_TEAMS: ReadonlyArray<{ key: ProTeam; label: string }> = [
  { key: 'a', label: 'A Team' },
  { key: 'b', label: 'B Team' },
  { key: 'c', label: 'C Team' },
  { key: 'production', label: 'Production Office' },
  { key: 'drivers', label: 'Drivers' },
];
export const PRO_TEAM_LABEL: Record<string, string> = { a: 'A Team', b: 'B Team', c: 'C Team', production: 'Production Office', drivers: 'Drivers' };

/** Pro-only seats offered in the Role menu + Add-crew picker on ACP accounts. */
export const PRO_SEAT_ROLES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'personal_assistant', label: 'Personal Assistant' },
  { value: 'security', label: 'Security' },
  { value: 'personal_trainer', label: 'Personal Trainer' },
  { value: 'monitor_engineer', label: 'Monitor Engineer' },
  { value: 'foh_systems_tech', label: 'FOH Systems Tech' },
  { value: 'monitor_systems_tech', label: 'Monitor Systems Tech' },
  { value: 'rf_tech', label: 'RF Tech' },
  { value: 'pa_tech_1', label: 'PA Tech 1' },
  { value: 'pa_tech_2', label: 'PA Tech 2' },
  { value: 'audio_comms_tech', label: 'Audio Comms Tech' },
  { value: 'backline', label: 'Backline' },
  { value: 'playback', label: 'Playback' },
  { value: 'driver', label: 'Driver' },
  { value: 'production_manager', label: 'Production Manager' },
  /* merch-roles 2026-07-28 — Pro-only assistant seat. */
  { value: 'merch_assistant', label: 'Merch Assistant' },
];

/** Auto team from role (Ryan's mapping). Manual `team` on the row overrides. */
export const TEAM_FOR_ROLE: Record<string, ProTeam> = {
  artist: 'a', band_member_av: 'a', personal_assistant: 'a', security: 'a',
  content_creator: 'a', personal_trainer: 'a',
  band_member: 'b',
  foh_engineer: 'c', monitor_engineer: 'c', foh_systems_tech: 'c',
  monitor_systems_tech: 'c', rf_tech: 'c', pa_tech_1: 'c', pa_tech_2: 'c',
  audio_comms_tech: 'c', backline: 'c', playback: 'c',
  driver: 'drivers',
  production_manager: 'production', tour_manager: 'production',
};

/** Resolve a person's Pro team: explicit override first, else role default. */
export function teamOf(p: { team?: string; role?: string }): ProTeam | null {
  const t = String(p.team || '').toLowerCase();
  if (t === 'a' || t === 'b' || t === 'c' || t === 'production' || t === 'drivers') return t;
  return TEAM_FOR_ROLE[String(p.role || '').toLowerCase()] || null;
}

/** Persist a contact's team override (same fail-closed blob write as
 *  setContactActingRole; '' clears the override back to the role default). */
export async function setContactTeam(id: string, team: string): Promise<void> {
  const res = await apiGet<{ value?: unknown; data?: unknown }>('auth', '/api/data/contacts'); // throws on failure
  let raw: unknown = res && ((res as { value?: unknown }).value !== undefined ? (res as { value?: unknown }).value
    : ((res as { data?: unknown }).data !== undefined ? (res as { data?: unknown }).data : res));
  if (raw && Array.isArray((raw as { value?: unknown }).value)) raw = (raw as { value: unknown[] }).value;
  if (!Array.isArray(raw)) throw new Error('Contacts load returned an unexpected shape, not saved');
  const arr = raw as Array<Record<string, unknown>>;
  const i = arr.findIndex((c) => String(c.id) === String(id));
  if (i >= 0) arr[i] = { ...arr[i], team: team || undefined };
  await apiPut('auth', '/api/data/contacts', { value: arr });
}

/* seat-role 2026-07-17 (Ryan), the REAL crew seats (vs the display-only
   acting roles above). Offered in the contact profile's Role menu; saving
   hits POST /api/team/member-role, which moves users.role AND the
   user_band_memberships seat together. */
export const SEAT_ROLES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'band_member', label: 'Band Member' },
  { value: 'band_member_av', label: 'Band Member + (full access)' },
  { value: 'tour_manager', label: 'Tour Manager' },
  { value: 'crew_member', label: 'Crew' },
  { value: 'foh_engineer', label: 'FOH Engineer' },
  { value: 'merch_manager', label: 'Merch Manager' },
  { value: 'content_creator', label: 'Content' },
];

/** Change a crew member's REAL seat. Requires the target's linked AC user id
 *  (CrewMember.userId), pending invites have no seat to change yet. */
export async function setMemberSeatRole(userId: string, role: string): Promise<void> {
  await apiPost('auth', '/api/team/member-role', { user_id: userId, role });
}

/** Who may SET a contact's acting-role: artist / Band Member AV / management
 *  (same gate as team editing). Everyone still SEES the resulting pill. */
export function canSetActingRole(user: User | null): boolean {
  return canEditTeams(user);
}

type RawContact = Contact & {
  role?: string; inviteStatus?: string; phone?: string; title?: string;
  userId?: string; teamUserId?: string; acting_role?: string; actingRole?: string;
  bio?: string; skills?: ReadonlyArray<string>; travel?: ContactTravel;
};

/** Map a raw /api/data/contacts row → CrewMember (mirrors renderContactRow). */
function toCrewMember(c: RawContact): CrewMember {
  let name = (c.firstName || '') + (c.lastName ? ' ' + c.lastName : '');
  if (!name.trim()) name = c.role || c.email || 'Unnamed';
  const avatarUrl = c.avatarUrl || c.avatar_url || c.photoUrl || c.photo_url || '';
  return {
    id: String(c.id ?? ''),
    name,
    role: String(c.role || ''),
    avatarUrl,
    pending: String(c.inviteStatus || '') === 'pending',
    email: String(c.email || ''),
    phone: String(c.phone || ''),
    title: String(c.title || ''),
    userId: String(c.userId || c.teamUserId || ''),
    actingRole: String(c.acting_role || c.actingRole || ''),
    team: String((c as Record<string, unknown>).team || ''),
    bio: String(c.bio || ''),
    skills: Array.isArray(c.skills) ? c.skills.map(String) : [],
    travel: (c.travel as ContactTravel) || {},
  };
}

/** Persist a contact's acting-role, read-modify-write the contacts blob
 *  (GET /api/data/contacts → set acting_role on the row → PUT {value}).
 *  audit 2026-07-02: fail closed, a failed GET now throws (was: [] → full-blob
 *  PUT that could wipe every contact) and PUT failures rethrow so the caller
 *  can roll back its optimistic state. */
export async function setContactActingRole(id: string, actingRole: string): Promise<void> {
  const res = await apiGet<{ value?: unknown; data?: unknown }>('auth', '/api/data/contacts'); // throws on failure
  let raw: unknown = res && ((res as { value?: unknown }).value !== undefined ? (res as { value?: unknown }).value
    : ((res as { data?: unknown }).data !== undefined ? (res as { data?: unknown }).data : res));
  if (raw && Array.isArray((raw as { value?: unknown }).value)) raw = (raw as { value: unknown[] }).value;
  if (!Array.isArray(raw)) throw new Error('Contacts load returned an unexpected shape, not saved');
  const arr = raw as Array<Record<string, unknown>>;
  const i = arr.findIndex((c) => String(c.id) === String(id));
  if (i >= 0) arr[i] = { ...arr[i], acting_role: actingRole || undefined };
  await apiPut('auth', '/api/data/contacts', { value: arr });
}

/** Remove a contact, read-modify-write the contacts blob (mirrors legacy
 *  contacts-wiring.js deleteContact: filter by id → PUT {value}).
 *  audit 2026-07-02: same fail-closed rule as setContactActingRole, a failed
 *  GET throws instead of PUTting a filtered [] over the whole contacts blob. */
export async function removeContact(id: string): Promise<void> {
  const res = await apiGet<{ value?: unknown; data?: unknown }>('auth', '/api/data/contacts'); // throws on failure
  let raw: unknown = res && ((res as { value?: unknown }).value !== undefined ? (res as { value?: unknown }).value
    : ((res as { data?: unknown }).data !== undefined ? (res as { data?: unknown }).data : res));
  if (raw && Array.isArray((raw as { value?: unknown }).value)) raw = (raw as { value: unknown[] }).value;
  if (!Array.isArray(raw)) throw new Error('Contacts load returned an unexpected shape, not removed');
  const next = (raw as Array<Record<string, unknown>>).filter((c) => String(c.id) !== String(id));
  await apiPut('auth', '/api/data/contacts', { value: next });
}

/** GET /api/data/contacts → crew rows. Reuses dashboard.fetchContacts(). */
export async function fetchCrew(): Promise<CrewMember[]> {
  const contacts = await fetchContacts();
  return contacts.map((c) => toCrewMember(c as RawContact));
}

// ── Teams (partner orgs) ──────────────────────────────────────────────────────
//
// VERIFICATION (Teams):
//   Legacy caller, contacts-wiring.js loadTeams (:1888-1902):
//     fetch(AUTH_WORKER + '/api/me/orgs?_=' + Date.now(),
//           { headers: { 'Authorization': 'Bearer ' + token } })
//       .then(r => r.ok ? r.json() : null)
//       .then(data => { var orgs = (data && data.orgs) || []; renderTeams(orgs); });
//   Worker return, cntrl-auth-worker.js handleMyOrgs (:6600), each org hydrated
//     at :6591-6598:
//       return jsonResponse({ ok: true, orgs: hydrated });
//       // hydrated[i] = { id, name, type, avatar_url, role, role_in_org }
//   Legacy client read, contacts-wiring.js renderTeams (:1920-1923):
//     var typeKey   = teamTypeKey(org.type);
//     var typeLabel = TEAM_TYPE_LABELS[String(org.type||'').toLowerCase()] || (org.type||'Partner');
//     var name      = org.name || 'Untitled team';
//
//   ⚠ Member count: CD's mock shows "3 members" / "6 members", but /api/me/orgs
//   does NOT return a member/roster count (see the verbatim hydrated shape above
//  , no member_count). The legacy renderTeams renders the TYPE label as the meta
//   line, not a count. So we render the type label as the meta (1:1 with legacy)
//   and leave the count as a TODO rather than fabricate a number.
//   TODO(data): member count per team, needs /api/me/orgs to return member_count
//   (the count exists on the org-detail endpoint cntrl-auth-worker.js:14018, not here).

/** A partner org row from /api/me/orgs, ready for CD's .team-row. */
export interface Team {
  readonly id: string;
  readonly name: string;
  /** Display type, "Label" | "Management" | "Booking" | raw. */
  readonly typeLabel: string;
  /** Org logo (label/manager's hydrated avatar) or '' → gradient initials. */
  readonly avatarUrl: string;
  /** Meta sub-line. The type label (legacy parity); count unavailable from this endpoint. */
  readonly meta: string;
}

const TEAM_TYPE_LABELS: Record<string, string> = {
  label: 'Label',
  manager: 'Management',
  management: 'Management',
  agent: 'Booking',
  booking: 'Booking',
  booking_agent: 'Booking',
};

interface RawOrg {
  readonly id?: string | number;
  readonly name?: string;
  readonly type?: string;
  readonly avatar_url?: string;
}

interface MyOrgsResponse {
  readonly ok?: boolean;
  readonly orgs?: ReadonlyArray<RawOrg>;
}

/** GET /api/me/orgs → partner-org team rows. Mirrors loadTeams + renderTeams. */
export async function fetchTeams(): Promise<Team[]> {
  if (!getToken()) return [];
  const res = await apiGet<MyOrgsResponse>('auth', '/api/me/orgs?_=' + Date.now()).catch(() => null);
  const orgs = res && Array.isArray(res.orgs) ? res.orgs : [];
  return orgs.map((org) => {
    const typeLabel =
      TEAM_TYPE_LABELS[String(org.type || '').toLowerCase()] || org.type || 'Partner';
    const name = org.name || 'Untitled team';
    return {
      id: String(org.id ?? ''),
      name,
      typeLabel,
      avatarUrl: org.avatar_url || '',
      // Legacy renders the type label as the meta line (renderTeams:1949). Member
      // count is not available from /api/me/orgs, see TODO(data) above.
      meta: typeLabel,
    };
  });
}

// ── Friends + Top 8 ───────────────────────────────────────────────────────────
//
// VERIFICATION (Friends):
//   Legacy caller, contacts-wiring.js loadFriends (:216):
//     fetch(AUTH_WORKER + '/api/friends', { headers: authHeaders(false), cache: 'no-store' })
//       .then(r => r.ok ? r.json() : null)
//       .then(res => { if (res && res.success && Array.isArray(res.friends)) friends = res.friends;
//                      else friends = []; });
//   Worker return, cntrl-auth-worker.js (GET /api/friends): jsonResponse with
//     { success: true, friends: [ { id, status, from_band, to_band, from_user_id,
//       to_user_id, from_name, to_name, … } ] }.
//   Legacy client read, getAcceptedFriends (:829-831) + friendDisplayName
//     (:841-850) + friendUserId (:852-859):
//     getAcceptedFriends → friends.filter(f => f.status === 'accepted');
//     friendDisplayName  → the band on whichever side ISN'T mine (pivot on
//                          band_name, not user.id), i.e. to_band/from_band.
//     friendUserId       → to_user_id/from_user_id of that other side.
//   Avatar, fetchBandPhoto(uid) wraps photoUrlForUser (:29-31):
//     STATS_WORKER + '/photo?u=<uid>&which=photo1'.
//     (photoUrlForSlug, STATS_WORKER + '/photo?slug=<slug>&which=photo1', is the
//      slug-keyed variant; the friend rows pivot on user id, so we use ?u=.)

/** A raw friend row from /api/friends (accepted + pending, both directions). */
export interface RawFriend {
  readonly id?: string | number;
  readonly status?: string;
  readonly from_band?: string;
  readonly to_band?: string;
  readonly from_name?: string;
  readonly to_name?: string;
  readonly from_user_id?: string | number;
  readonly to_user_id?: string | number;
  readonly user_id?: string | number;
  readonly band_name?: string;
  readonly name?: string;
  readonly role?: string;
}

interface FriendsResponse {
  readonly success?: boolean;
  readonly friends?: ReadonlyArray<RawFriend>;
}

/** A friend ready to render into CD's .friend-row / .top8-tile. */
export interface Friend {
  readonly id: string;
  /** The other band's display name (the side that isn't mine). */
  readonly name: string;
  /** Band photo URL (?u=<uid>) or '' → gradient initials. */
  readonly avatarUrl: string;
}

/** GET /api/friends → ALL friend rows (filtered later). Mirrors loadFriends. */
export async function fetchFriendRows(): Promise<RawFriend[]> {
  if (!getToken()) return [];
  const res = await apiGet<FriendsResponse>('auth', '/api/friends').catch(() => null);
  return res && res.success && Array.isArray(res.friends) ? res.friends.slice() : [];
}

/** The per-user profile served by GET /api/users/:id/profile. The contact ROW
 *  (/api/data/contacts) is a lightweight pointer; a person's real bio / skills /
 *  travel live on their users row and are read through this visibility-gated
 *  endpoint. `travel` is SERVER-gated, it's null unless the viewer is the user
 *  themselves or a trusted same-band role (artist/manager/tour_manager/booking_
 *  agent), so the client renders it whenever it's non-null (no second guess). */
export interface PersonProfile {
  readonly bio: string;
  readonly skills: readonly string[];
  readonly email: string;
  readonly phone: string;
  readonly avatar_url: string;
  readonly location: string;
  readonly display_name: string;
  readonly current_band: string;
  /** 'friends' | 'private', who may see this profile. */
  readonly profile_visibility: string;
  /** null when the viewer isn't authorized to see travel (server decision). */
  readonly travel: ContactTravel | null;
}

/** The body PUT /api/me/profile accepts (the worker whitelist, ~22480). */
export interface MyProfileSave {
  readonly display_name: string;
  readonly phone: string;
  readonly bio: string;
  readonly avatar_url: string;
  readonly location: string;
  readonly skills: readonly string[];
  readonly profile_visibility: string;
  readonly travel_known_traveler_number: string;
  readonly travel_redress_number: string;
  readonly travel_airline_loyalty: readonly LoyaltyProgram[];
  readonly travel_hotel_loyalty: readonly LoyaltyProgram[];
}

/** My own profile, resolve my id from /api/me, then read the (self) gated
 *  profile (travel always present for self). For the "My profile" editor.
 *
 *  The /api/me user is ALSO used as a fallback for the identity fields
 *  (display_name / email / avatar_url / location / phone / current_band): the
 *  self profile blob can come back empty or with success:false on some accounts
 *  (e.g. a band-owner seat on the artist surface), which used to leave the editor
 *  blank ("shows nothing"). The roster "My profile" modal already merges /api/me
 *  this way; this brings the artist-surface editor to parity, and guarantees a
 *  non-null return whenever /api/me succeeds so the modal never hangs on empty. */
export async function fetchMyProfile(): Promise<PersonProfile | null> {
  const me = await apiGet<{
    user?: {
      id?: string | number;
      display_name?: string;
      email?: string;
      avatar_url?: string;
      location?: string;
      phone?: string;
      band_name?: string;
    };
  }>('auth', '/api/me').catch(() => null);
  const u = me && me.user ? me.user : null;
  const id = u && u.id != null ? String(u.id) : '';
  if (!id) return null;

  const p = await fetchPersonProfile(id);
  // Merge: prefer the gated profile blob, fall back to the /api/me identity
  // fields. Never return null when we have a signed-in user, the editor should
  // always at least show name / email / avatar so it's editable and non-blank.
  return {
    bio: p?.bio || '',
    skills: p?.skills ? p.skills.slice() : [],
    email: p?.email || (u && u.email) || '',
    phone: p?.phone || (u && u.phone) || '',
    avatar_url: p?.avatar_url || (u && u.avatar_url) || '',
    location: p?.location || (u && u.location) || '',
    display_name: p?.display_name || (u && u.display_name) || '',
    current_band: p?.current_band || (u && u.band_name) || '',
    profile_visibility: p?.profile_visibility || 'friends',
    travel: p?.travel || null,
  };
}

/** PUT /api/me/profile, persist the My-profile editor (self-only). Returns
 *  true on success. Worker: handleUpdateMyProfile whitelists each field. */
export async function saveMyProfile(payload: MyProfileSave): Promise<boolean> {
  try { await apiPut('auth', '/api/me/profile', payload); return true; } catch { return false; }
}

/** GET /api/users/:id/profile → the gated person profile (or null on miss/403).
 *  Worker return, handleGetUserProfile (cntrl-auth-worker.js:23353):
 *    { success:true, profile:{ bio, skills:[…], travel:{…}|null, email, phone,
 *      avatar_url, location, display_name, current_band, … } } */
export async function fetchPersonProfile(userId: string): Promise<PersonProfile | null> {
  if (!userId || !getToken()) return null;
  const res = await apiGet<{ success?: boolean; profile?: Partial<PersonProfile> & { skills?: unknown; travel?: unknown } }>(
    'auth', '/api/users/' + encodeURIComponent(userId) + '/profile',
  ).catch(() => null);
  const p = res && res.success ? res.profile : null;
  if (!p) return null;
  return {
    bio: String(p.bio || ''),
    skills: Array.isArray(p.skills) ? (p.skills as unknown[]).map(String) : [],
    email: String(p.email || ''),
    phone: String(p.phone || ''),
    avatar_url: String(p.avatar_url || ''),
    location: String(p.location || ''),
    display_name: String(p.display_name || ''),
    current_band: String(p.current_band || ''),
    profile_visibility: String((p as { profile_visibility?: string }).profile_visibility || 'friends'),
    travel: p.travel && typeof p.travel === 'object' ? (p.travel as ContactTravel) : null,
  };
}

/** A friend band's profile, assembled for the artist-profile modal. */
export interface BandShowRow { readonly id: string; readonly date: string; readonly venue: string; readonly loc: string; readonly fest: boolean; }
export interface BandView {
  readonly name: string;
  readonly genres: readonly string[];
  readonly location: string;
  readonly photoUrl: string;
  readonly members: CrewMember[];
  readonly friends: Friend[];
  readonly shows: BandShowRow[];
}

/** Fetch a friend band's view-model by slug, roster (members + genres +
 *  location) + the band's friends + upcoming shows, in parallel. Mirrors the
 *  legacy openBand fan-out (profile-wiring.js:1134). Worker returns:
 *    /roster  → { band: { name, slug, primary_user_id, members:[{user_id,
 *                 display_name, role, avatar_url}], genres:[], location } }
 *    /friends → { friends: [{ band_name, slug }] }
 *    /shows   → { shows: [{ id, date, name, city }] } */
export async function fetchBandView(slug: string): Promise<BandView | null> {
  if (!slug || !getToken()) return null;
  const base = '/api/bands/' + encodeURIComponent(slug);
  const [r, fr, sh] = await Promise.all([
    apiGet<{ band?: Record<string, unknown> }>('auth', base + '/roster').catch(() => null),
    apiGet<{ friends?: Array<Record<string, unknown>> }>('auth', base + '/friends').catch(() => null),
    apiGet<{ shows?: Array<Record<string, unknown>> }>('auth', base + '/shows').catch(() => null),
  ]);
  const band = r && r.band;
  if (!band) return null;
  const primaryId = band.primary_user_id != null ? String(band.primary_user_id) : '';
  /* band-tile-photo 2026-07-17 (Ryan: "we need to be using the Artists main
     photo here. RK is using their logo?"), which=photo1 is a PHYSICAL slot
     cached 24h at the edge; a band that uploaded their logo before their
     photos (RK) had the logo fallback baked into every tile for a day.
     antenna_main is the band's CHOSEN avatar slot with 2-minute freshness,
     and size=sm serves the ~720px variant instead of the 2800px original. */
  const photoForUid = (uid: string): string => (uid ? STATS_WORKER + '/photo?u=' + encodeURIComponent(uid) + '&which=antenna_main&size=sm' : '');
  const photoForSlug = (s: string): string => (s ? STATS_WORKER + '/photo?slug=' + encodeURIComponent(s) + '&which=antenna_main&size=sm' : '');
  /* primary-hidden 2026-07-27 (Ryan: "Primary artist account shouldnt show
     in members when you click on an artist in friends list") — the band
     PRIMARY is an invite-only shell; a friend band's Members list shows the
     humans (AV + members), not the shell. Matched by primary_user_id AND
     role='artist' for legacy rows where primary_user_id is missing. */
  const members: CrewMember[] = (Array.isArray(band.members) ? band.members : []).filter((m: Record<string, unknown>) => {
    const uid = String(m.user_id ?? '');
    if (primaryId && uid === primaryId) return false;
    if (String(m.role || '').toLowerCase() === 'artist') return false;
    return true;
  }).map((m: Record<string, unknown>) => {
    const uid = String(m.user_id ?? '');
    // The primary (artist) seat falls back to the band's photo1 ("Photo 1 is the
    // profile photo for every artist"); teammates without a personal avatar keep
    // the gradient initials.
    const avatarUrl = String(m.avatar_url || '') || (uid && uid === primaryId ? photoForUid(uid) : '');
    return {
      id: uid, name: String(m.display_name || ''), role: String(m.role || ''),
      avatarUrl, pending: false, email: '', phone: '', title: '',
      userId: uid, actingRole: '', team: '', bio: '', skills: [] as string[], travel: {},
    };
  });
  const friends: Friend[] = ((fr && Array.isArray(fr.friends)) ? fr.friends : []).map((f: Record<string, unknown>) => {
    const fslug = String(f.slug || '') || bandSlug(String(f.band_name || ''));
    return { id: fslug, name: String(f.band_name || ''), avatarUrl: photoForSlug(fslug) };
  });
  const shows: BandShowRow[] = ((sh && Array.isArray(sh.shows)) ? sh.shows : []).map((s: Record<string, unknown>) => ({
    id: String(s.id ?? ''), date: String(s.date || ''), venue: String(s.name || s.venue || ''),
    loc: String(s.city || ''), fest: /fest/i.test(String(s.name || '')),
  }));
  return {
    name: String(band.name || ''),
    genres: Array.isArray(band.genres) ? band.genres.map(String) : [],
    location: String(band.location || ''),
    photoUrl: primaryId ? STATS_WORKER + '/photo?u=' + encodeURIComponent(primaryId) + '&which=antenna_main&size=sm' : '',
    members, friends, shows,
  };
}

/** band name → slug (matches the worker's slugify). */
export function bandSlug(name: string): string {
  return String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/** Friends pivot on band name, not the viewer's user id (contacts-wiring.js:837). */
function myBandLc(user: User | null): string {
  return String(user?.band_name || '').toLowerCase();
}

/** The other band's name, the side that isn't mine (friendDisplayName :841-850). */
function friendDisplayName(f: RawFriend, mine: string): string {
  const fromLc = String(f.from_band || '').toLowerCase();
  const toLc = String(f.to_band || '').toLowerCase();
  if (mine && fromLc === mine) return f.to_band || f.to_name || '';
  if (mine && toLc === mine) return f.from_band || f.from_name || '';
  return f.to_band || f.from_band || f.band_name || f.name || '';
}

/** The other band's user id, for the photo lookup (friendUserId :852-859). */
function friendUserId(f: RawFriend, mine: string): string | number | null {
  const fromLc = String(f.from_band || '').toLowerCase();
  const toLc = String(f.to_band || '').toLowerCase();
  if (mine && fromLc === mine) return f.to_user_id ?? null;
  if (mine && toLc === mine) return f.from_user_id ?? null;
  return f.to_user_id ?? f.user_id ?? null;
}

/** Band photo URL keyed by user id, STATS_WORKER '/photo?u=<uid>&which=photo1'
 *  (photoUrlForUser :29-31, wrapped by fetchBandPhoto :37-42). '' when no uid. */
function bandPhotoUrlForUid(uid: string | number | null): string {
  if (uid == null || uid === '') return '';
  return STATS_WORKER + '/photo?u=' + encodeURIComponent(String(uid)) + '&which=antenna_main&size=sm';
}

/** Accepted friends only (getAcceptedFriends :829-831), mapped to view models. */
export function acceptedFriends(rows: ReadonlyArray<RawFriend>, user: User | null): Friend[] {
  const mine = myBandLc(user);
  return rows
    .filter((f) => f.status === 'accepted')
    .map((f) => {
      const name = friendDisplayName(f, mine) || 'Friend';
      return {
        id: String(f.id ?? ''),
        name,
        avatarUrl: bandPhotoUrlForUid(friendUserId(f, mine)),
      };
    });
}

/**
 * Top 8 = the first 8 accepted friends (legacy renderTop8 :877-899, default
 * branch where no saved top8Order exists: `accepted.slice(0,8)`). Returns an
 * 8-length array; empty slots are null → CD's "drop here" placeholder.
 *
 * This is the DEFAULT render used when no saved order exists. When a saved
 * order is present, the screen lifts an 8-length id array (top8Order) and
 * resolves it against the accepted friends via slotsFromOrder() below, both
 * mirror legacy renderTop8 (:877-933).
 */
export function deriveTop8(friends: ReadonlyArray<Friend>): (Friend | null)[] {
  const slots: (Friend | null)[] = friends.slice(0, 8);
  while (slots.length < 8) slots.push(null);
  return slots;
}

// ── Top 8 saved order: load / save / resolve ──────────────────────────────────
//
// VERIFICATION (Top 8 GET/PUT), see the strict verification block in the task
// report; in brief:
//   Legacy LOAD caller, contacts-wiring.js loadContactsAndTop8 (:184) batches it:
//     fetch(AUTH_WORKER + '/api/data?keys=contacts,top8', { headers: authHeaders(false) })
//       → res.success && res.data, then applyContactsBatch (:170-178):
//         var t8 = unwrap(batch.top8);  top8Order = Array.isArray(t8) ? t8.slice(0,8) : [];
//     The SINGULAR equivalent (same blob envelope) is GET /api/data/top8.
//   Legacy SAVE caller, contacts-wiring.js saveTop8Order (:347-354):
//     fetch(AUTH_WORKER + '/api/data/top8', { method:'PUT', headers: authHeaders(true),
//           body: JSON.stringify({ value: top8Order }) })
//   Worker GET return, cntrl-auth-worker.js handleGetUserData (:18553-18557):
//     return jsonResponse({ success: true, key: dataKey, data: data || {} });
//     where `data` is the stored blob { value: [ids] } (top8 is whitelisted in
//     worker/src/config.js BAND_SHARED_DATA_KEYS :378). So for the singular GET,
//     res.data.value carries the id array.
//   Worker PUT, same /api/data/:key route stores the posted { value } blob.

/** A top8 id array: exactly 8 entries; null = empty slot. */
export type Top8Order = (string | null)[];

interface Top8Response {
  readonly success?: boolean;
  readonly key?: string;
  /** The stored blob { value: [...ids] } (may be {} when nothing saved yet). */
  readonly data?: { value?: ReadonlyArray<string | null> } | null;
}

/** Pad/truncate any id list to an exactly-8-length Top8Order. */
function padTo8(ids: ReadonlyArray<string | null>): Top8Order {
  const out: Top8Order = ids.slice(0, 8).map((v) => (v == null || v === '' ? null : String(v)));
  while (out.length < 8) out.push(null);
  return out;
}

/**
 * GET /api/data/top8 → the saved 8-id order (padded to 8 with nulls), or `null`
 * when nothing is saved (so the caller falls back to deriveTop8's default).
 * Mirrors loadContactsAndTop8/applyContactsBatch's unwrap(batch.top8) (:170-178)
 *, here against the singular blob envelope { success, key, data:{ value } }.
 */
export async function fetchTop8(): Promise<Top8Order | null> {
  if (!getToken()) return null;
  const res = await apiGet<Top8Response>('auth', '/api/data/top8').catch(() => null);
  const value = res && res.success && res.data ? res.data.value : undefined;
  if (!Array.isArray(value) || value.length === 0) return null;
  return padTo8(value);
}

/**
 * PUT /api/data/top8 { value: order }, persist the 8-id order. Mirrors
 * saveTop8Order (:347-354) exactly (apiPut sends authHeaders('PUT') =
 * authHeaders(true): Bearer + Content-Type). No-op when signed out.
 */
export async function saveTop8(order: Top8Order): Promise<void> {
  if (!getToken()) return;
  await apiPut('auth', '/api/data/top8', { value: order }).catch(() => undefined);
}

/**
 * Resolve an 8-id order against the accepted friends → 8 slots of (Friend|null).
 * Mirrors renderTop8's `byId` resolution (:917-921): unknown / vacated ids
 * resolve to null (CD's "drop here"). When no saved order exists, callers pass
 * the default (first-8 friend ids) so the result equals deriveTop8.
 */
export function slotsFromOrder(
  order: ReadonlyArray<string | null>,
  friends: ReadonlyArray<Friend>,
): (Friend | null)[] {
  const byId: Record<string, Friend> = {};
  friends.forEach((f) => {
    byId[f.id] = f;
  });
  const padded = padTo8(order);
  return padded.map((id) => (id != null ? byId[id] ?? null : null));
}

/** The default 8-id order (first-8 accepted friend ids), padded to 8 with nulls.
 *  Mirrors renderTop8's no-saved-order branch (:891-892). */
export function defaultTop8Order(friends: ReadonlyArray<Friend>): Top8Order {
  return padTo8(friends.slice(0, 8).map((f) => f.id));
}

// ── Your Teams role-gate ──────────────────────────────────────────────────────
//
// Per the owner's rule: ONLY the owner tier (Artist / Band Member AV / the band
// founder, plus jedi god-mode) can ADD/EDIT teams; ALL other roles still SEE the
// teams list but VIEW-ONLY. Effective role in React = the /api/me user.role
// (legacy getRealRole returns user.role, role-perms.js:678). user.role is the
// system level (artist/band_member/jedi); user.membership_role is the seat the
// person was invited as; user.is_band_primary marks the founder.
//
// NOTE: there is no jedi "view-as" in React yet, so the OWNER can't preview the
// view-only state from their own login, that's expected; a real lower-role
// teammate (manager / tour_manager / band_member / crew / content / merch) gets
// the view-only state because their user.role + membership_role fail all the
// clauses below.

/**
 * True when the signed-in user may add/edit Your Teams (owner/admin tier);
 * false → view-only (team list still renders, controls hidden).
 */
export function canEditTeams(user: User | null): boolean {
  if (!user) return false;
  return (
    user.role === 'artist' ||
    user.role === 'jedi' ||
    user.membership_role === 'band_member_av' ||
    user.membership_role === 'artist' ||
    user.is_band_primary === true
  );
}

// ── Friend lookup + request (Add friend) ─────────────────────────────────────
//
// VERIFICATION (Add friend):
//   The legacy Add-friend modal finds the OTHER band by EMAIL, then sends the
//   request keyed on the band it confirmed:
//     1) lookup, contacts-wiring.js runLookup (:1618):
//          fetch(AUTH_WORKER + '/api/friends/lookup-email?email=' + enc,
//                { headers: authHeaders(false) })
//        → { success, exists, bandName, bandPrimaryId, self } (:1626-1635).
//     2) request, contacts-wiring.js addBtn handler (:1675):
//          fetch(AUTH_WORKER + '/api/friends/request', { method:'POST',
//                headers: authHeaders(true),
//                body: JSON.stringify({ bandName, bandPrimaryId }) })
//        → { success } (:1684). Posting bandPrimaryId (not just the name) lands
//        the request on the EXACT silo the viewer confirmed by email (CRIT-20).

/** Result of GET /api/friends/lookup-email, the band (if any) behind an email. */
export interface FriendLookup {
  /** True when the email maps to a band on Artist CNTRL. */
  readonly exists: boolean;
  /** The matched band's display name ('' when no match). */
  readonly bandName: string;
  /** The matched band's primary user id, posted to /api/friends/request so the
   *  request lands on the exact silo (CRIT-20). '' when no match. */
  readonly bandPrimaryId: string;
  /** True when the email is the viewer's own, can't friend yourself. */
  readonly self: boolean;
}

interface FriendLookupResponse {
  readonly success?: boolean;
  readonly error?: string;
  readonly exists?: boolean;
  readonly bandName?: string;
  readonly bandPrimaryId?: string | number;
  readonly self?: boolean;
}

/**
 * Look up the band behind an email. Mirrors runLookup (:1618). Throws on a
 * non-2xx (apiGet) or a `{ success:false }` worker body so the modal can surface
 * the real error rather than silently rendering "no match".
 */
export async function lookupFriendByEmail(email: string): Promise<FriendLookup> {
  const res = await apiGet<FriendLookupResponse>(
    'auth',
    '/api/friends/lookup-email?email=' + encodeURIComponent(email),
  );
  if (!res || !res.success) throw new Error((res && res.error) || 'Lookup failed.');
  return {
    exists: !!res.exists,
    bandName: String(res.bandName || ''),
    bandPrimaryId: res.bandPrimaryId != null ? String(res.bandPrimaryId) : '',
    self: !!res.self,
  };
}

/**
 * Send a friend request to a band. Mirrors the legacy addBtn handler (:1675):
 * POST /api/friends/request { bandName, bandPrimaryId }. Posting bandPrimaryId
 * targets the exact silo confirmed by email (CRIT-20); bandName is kept as the
 * legacy fallback. apiPost carries X-Band-Id + checks 2xx; we also assert the
 * worker's `{ success:true }` so a 200-with-error can't read as success.
 */
export async function sendFriendRequest(bandName: string, bandPrimaryId: string): Promise<void> {
  if (!bandName && !bandPrimaryId) throw new Error('No band to friend.');
  const res = await apiPost<{ success?: boolean; error?: string }>('auth', '/api/friends/request', {
    bandName,
    bandPrimaryId,
  });
  if (!res || !res.success) throw new Error((res && res.error) || 'Could not send request.');
}

// ── Invite a teammate / crew member (Add crew · Invite team) ──────────────────
//
// VERIFICATION (Invite):
//   Legacy caller, contacts-wiring.js _sendInvite (:788):
//     fetch(AUTH_WORKER + '/api/invite', { method:'POST', headers: authHeaders(true),
//           body: JSON.stringify({ email: email, role: role }) })
//       → { success, invite_link, email_sent } (worker handleCreateInvite,
//         cntrl-auth-worker.js:17348). The worker whitelists role against
//         ['band_member','tour_manager','booking_agent','manager','label',
//          'artist','crew_member','foh_engineer','content_creator'] (:17372).
//   Crew vs team differ ONLY by which role you send (crew/touring roles vs the
//   partner-org roles); the endpoint + payload are identical.

/** The backend-accepted invite roles (worker handleCreateInvite :17372). */
export const INVITE_ROLES = [
  'band_member',
  'band_member_av',
  'tour_manager',
  'booking_agent',
  'manager',
  'label',
  'artist',
  'crew_member',
  'foh_engineer',
  'merch_manager',
  'content_creator',
  /* acpro-teams 2026-07-27 — Pro crew seats (worker whitelists updated in
     the same change: handleCreateInvite, member-role, config VALID_ROLES). */
  'personal_assistant',
  'security',
  'personal_trainer',
  'monitor_engineer',
  'foh_systems_tech',
  'monitor_systems_tech',
  'rf_tech',
  'pa_tech_1',
  'pa_tech_2',
  'audio_comms_tech',
  'backline',
  'playback',
  'driver',
  'production_manager',
  'merch_assistant',
] as const;
export type InviteRole = (typeof INVITE_ROLES)[number];

/** Crew-side roles (Your Crew): the day-to-day touring/band seats. */
export const CREW_INVITE_ROLES: ReadonlyArray<{ value: InviteRole; label: string }> = [
  { value: 'band_member', label: 'Band Member' },
  /* av-invite 2026-07-16 (Ryan, RK onboarding): band_member_av is the
     co-equal artist-tier seat (FULL tabs, same as the owner), it existed in
     the permission model but was never offered in this dropdown. Listed
     SECOND so the safe limited seat stays the default (roles[0]). */
  { value: 'band_member_av', label: 'Band Member · Full access' },
  { value: 'tour_manager', label: 'Tour Manager' },
  { value: 'crew_member', label: 'Crew' },
  { value: 'foh_engineer', label: 'FOH Engineer' },
  /* merch-roles 2026-07-28 (Ryan: "ac & pro need Merch Manager for crew
     roles") — the seat existed but was never offered in this picker. */
  { value: 'merch_manager', label: 'Merch Manager' },
  { value: 'content_creator', label: 'Content' },
];

/** acpro-teams 2026-07-27 — the Add-crew picker on a PRO account: the
 *  standard crew seats plus every Pro seat. Standard AC keeps CREW_INVITE_ROLES. */
export const PRO_CREW_INVITE_ROLES: ReadonlyArray<{ value: InviteRole; label: string }> = [
  ...CREW_INVITE_ROLES,
  ...(PRO_SEAT_ROLES as ReadonlyArray<{ value: InviteRole; label: string }>),
];

/** Team-side roles (Your Teams): the partner-org seats. */
export const TEAM_INVITE_ROLES: ReadonlyArray<{ value: InviteRole; label: string }> = [
  { value: 'manager', label: 'Management' },
  { value: 'label', label: 'Label' },
  { value: 'booking_agent', label: 'Booking' },
];

/** Result of POST /api/invite, whether the email actually went out + the link. */
export interface InviteResult {
  /** The worker successfully sent the invite email. */
  readonly emailSent: boolean;
  /** The join link (always present even when the email send failed). */
  readonly inviteLink: string;
}

/**
 * Invite a teammate by email + role. Mirrors _sendInvite (:788): POST /api/invite
 * { email, role }. Used by BOTH "Add crew" and "Invite team", they differ only
 * by the role passed. apiPost carries X-Band-Id + checks 2xx; we also assert the
 * worker's `{ success:true }` so a 200-with-error can't read as success. The
 * caller surfaces the returned link when email_sent is false (legacy parity).
 */
export async function inviteMember(email: string, role: InviteRole, firstName?: string, lastName?: string): Promise<InviteResult> {
  const e = email.trim().toLowerCase();
  if (!e) throw new Error('Email is required.');
  /* invite-names 2026-07-30 (Ryan: "we should add first and last name here")
     — names ride in the POST for forward-compat (the worker reads only
     email/role today and ignores extras); their real home is the pending
     contact row (upsertPendingInviteContact below). */
  const res = await apiPost<{ success?: boolean; error?: string; email_sent?: boolean; invite_link?: string }>(
    'auth',
    '/api/invite',
    { email: e, role, first_name: (firstName || '').trim(), last_name: (lastName || '').trim() },
  );
  if (!res || !res.success) throw new Error((res && res.error) || 'Could not send invite.');
  return { emailSent: !!res.email_sent, inviteLink: String(res.invite_link || '') };
}

/** invite-fix 2026-07-02, after a successful POST /api/invite, the LEGACY flow
 *  persisted a pending contact row (contacts-wiring.js persistPerson :1339-1351
 *  shape, then _sendInvite :796-797 stamps inviteStatus='pending' + inviteRole)
 *  so the invitee appeared in Your Crew immediately AND the worker's accept-time
 *  healing (healContactsBlob, matches rows by lowercased email / userId, flips
 *  'pending'/'sent' → 'joined') had a row to heal. The React InviteModal only
 *  POSTed the invite, so it never surfaced in Your Crew. This upserts that row
 *  via the same fail-closed read-modify-write as the other contact writes.
 *
 *  Dedupe: rows match on lowercased email (the same key the worker heals on).
 *  A re-invite refreshes the existing row's inviteStatus/inviteRole in place,
 *  never a duplicate, and a row already 'joined' is left untouched (no
 *  regression back to pending). Throws on failure so the caller can show the
 *  non-fatal "will appear on accept" note (the invite itself already succeeded). */
export async function upsertPendingInviteContact(email: string, role: InviteRole, firstName?: string, lastName?: string): Promise<void> {
  const e = email.trim().toLowerCase();
  const fn = (firstName || '').trim();
  const ln = (lastName || '').trim();
  if (!e) return;
  const res = await apiGet<{ value?: unknown; data?: unknown }>('auth', '/api/data/contacts'); // throws on failure
  let raw: unknown = res && ((res as { value?: unknown }).value !== undefined ? (res as { value?: unknown }).value
    : ((res as { data?: unknown }).data !== undefined ? (res as { data?: unknown }).data : res));
  if (raw && Array.isArray((raw as { value?: unknown }).value)) raw = (raw as { value: unknown[] }).value;
  // A band with NO contacts blob yet gets data:{} from the worker (handleGetUserData
  // returns `data || {}`), the GET succeeded, so that's a GENUINE empty list, not a
  // failure: seed []. Any other non-array shape still fails closed (same rule as
  // setContactActingRole / removeContact above).
  if (raw && typeof raw === 'object' && !Array.isArray(raw) && Object.keys(raw as object).length === 0) raw = [];
  if (!Array.isArray(raw)) throw new Error('Contacts load returned an unexpected shape, not saved');
  const arr = raw as Array<Record<string, unknown>>;
  const i = arr.findIndex((c) => String((c && c.email) || '').trim().toLowerCase() === e);
  if (i >= 0) {
    if (String(arr[i].inviteStatus || '') === 'joined') return; // already a member, nothing to do
    // Re-invite: refresh the flags on the existing row (legacy _sendInvite :796-797).
    arr[i] = { ...arr[i], inviteStatus: 'pending', inviteRole: role,
      /* invite-names 2026-07-30: a re-invite may supply names the original
         row lacked; never blank existing names with empty input. */
      ...(fn ? { firstName: fn } : {}), ...(ln ? { lastName: ln } : {}) };
  } else {
    // EXACT legacy persistPerson row shape (:1339-1348) + the invite flags, the
    // worker's accept-time healing expects these fields. No name captured by the
    // invite modal, so the display name falls back to role/email (renderContactRow
    // parity, mirrored in toCrewMember above).
    arr.push({
      id: 'c_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7),
      type: 'person',
      firstName: fn,
      lastName: ln,
      role,
      email: e,
      phone: '',
      createdAt: new Date().toISOString(),
      inviteStatus: 'pending',
      inviteRole: role,
    });
  }
  await apiPut('auth', '/api/data/contacts', { value: arr });
}

// ── Aggregate fetch ───────────────────────────────────────────────────────────

/** Everything CD's Contacts screen renders, in one typed bundle. */
export interface ContactsData {
  readonly user: User | null;
  readonly crew: CrewMember[];
  readonly teams: Team[];
  readonly friends: Friend[];
  /** The resolved 8 slots for the INITIAL render (saved order if any, else default). */
  readonly top8: (Friend | null)[];
  /** The initial 8-id order the screen lifts into state (saved or default-from-friends). */
  readonly top8Order: Top8Order;
}

/**
 * Fetch all four Contacts cards in parallel. Each fetcher already fails soft to
 * an empty list, so the screen always renders honest empty states (Bible 06.4)
 * rather than throwing. `user` is passed in (already resolved by the screen via
 * fetchCurrentUser) so the friend-name pivot has the viewer's band name.
 */
export async function fetchContactsData(user: User | null): Promise<ContactsData> {
  const [crew, teams, friendRows, savedOrder] = await Promise.all([
    fetchCrew().catch(() => [] as CrewMember[]),
    fetchTeams().catch(() => [] as Team[]),
    fetchFriendRows().catch(() => [] as RawFriend[]),
    fetchTop8().catch(() => null),
  ]);
  const friends = acceptedFriends(friendRows, user);
  // renderTop8 (:885-892): saved order wins (padded to 8); else first-8 friend ids.
  const top8Order = savedOrder ?? defaultTop8Order(friends);
  return {
    user,
    crew,
    teams,
    friends,
    top8: slotsFromOrder(top8Order, friends),
    top8Order,
  };
}
